<script setup>
import { onMounted, ref, watchEffect, computed } from "vue";
import AddToCalendarButton from "../components/AddToCalendarButton.vue";
import { isRequired, validateEmail } from "@/rules";
import { fetchWithHeaders } from "@/utils";
import { auth } from "@/plugins/keycloak";
import { toDjangoDate, dayjs, toDate, toTime, previousOpenDayAt, sessionFormat } from "@/filters";
import { useQuasar } from "quasar";
import ConsultationSlotSelect from "@/components/ConsultationSlotSelect.vue";

const $q = useQuasar();
const sessions = ref(null);
const selectedSession = ref();
const selectedSlot = ref();
const step = ref(1);
const userDataFetched = ref(false);
const newRegistration = ref(true);
const registration = ref({
    user_email: "",
    user_data: {
        givenName: "",
        surname: "",
        phone: "",
        plan: "",
        etape: "",
        matricule: "",
        pernum: "",
    },
    comment: "",
});
const isNoShow = ref(false);
const hasFutureRegistration = ref(false);

async function checkEmail() {
    const email = registration.value.user_email?.trim();
    if (!email || !selectedSession.value) {
        isNoShow.value = false;
        hasFutureRegistration.value = false;
        return;
    }
    try {
        const result = await fetchWithHeaders(
            `${window._env_.API_ENDPOINT}/registrations/check-email/?email=${encodeURIComponent(email)}&session=${selectedSession.value.code}`
        );
        if (result) {
            isNoShow.value = result.has_no_show || false;
            hasFutureRegistration.value = result.has_future_registration || false;
        }
    } catch (e) {
        // Silently fail - the backend validation will catch it anyway
    }
}

onMounted(async () => {
    // TODO: filter sessions by date (date de début et date de fin) and state.
    const url = `${window._env_.API_ENDPOINT}/sessions/?state=O&open_date__lte=${toDjangoDate(
        dayjs()
    )}&close_date__gte=${toDjangoDate(dayjs())}`;
    sessions.value = await fetchWithHeaders(url);
    if (sessions.value.length === 1) {
        selectedSession.value = sessions.value[0];
    }
});
watchEffect(async () => {
    if (auth.isAuthenticated.value && !auth.isStaff.value) {
        registration.value.user_email = auth.tokenParsed.value.email;
        registration.value.user_data.givenName = auth.tokenParsed.value.givenName;
        registration.value.user_data.surname = auth.tokenParsed.value.surname;
        registration.value.user_data.pernum = auth.tokenParsed.value.employeeNumber;
        registration.value.user_data.matricule = auth.tokenParsed.value.swissEduPersonMatriculationNumber;
    }
});
watchEffect(async () => {
    if (selectedSession.value && auth.isAuthenticated.value && !auth.isStaff.value) {
        // Vérifier le statut de l'email (no-show, réservation existante)
        await checkEmail();

        if (hasFutureRegistration.value) {
            // Charger la réservation existante pour l'afficher
            const url = `${window._env_.API_ENDPOINT}/registrations/?slot__session=${
                selectedSession.value.code
            }&slot__start__gte=${dayjs().toISOString()}`;
            const results = await fetchWithHeaders(url);
            if (results.length > 0) {
                registration.value = results[0];
                selectedSlot.value = await fetchWithHeaders(`${window._env_.API_ENDPOINT}/slots/${registration.value.slot}/`);
                step.value = 4;
                newRegistration.value = false;
            }
        } else if (!isNoShow.value) {
            if (registration.value.user_data.matricule && !userDataFetched.value) {
                // load additional data for logged in users
                const userData = await fetchWithHeaders(
                    `${window._env_.API_ENDPOINT}/students/${registration.value.user_data.matricule}/?session=${selectedSession.value.code}`
                );
                if (userData && !userData.error) {
                    registration.value.user_data = Object.assign({}, registration.value.user_data, userData);
                }
                userDataFetched.value = true;
            }
        }
    }
});

const canCancel = computed(() => {
    if (!selectedSlot.value) {
        return false;
    }
    // compute cancel time day before not week-end
    return new Date().getTime() < previousOpenDayAt(selectedSlot.value.start, window._env_.CANCEL_TIME_DAY_BEFORE_HOUR).valueOf();
});

async function submitRegistration() {
    const url = `${window._env_.API_ENDPOINT}/registrations/`;
    // Préparer les données sans le seat (le backend l'assignera automatiquement)
    const payload = {
        slot: selectedSlot.value.pk,
        user_email: registration.value.user_email,
        user_data: registration.value.user_data,
        comment: registration.value.comment,
    };

    fetchWithHeaders(
        url,
        {
            method: "POST",
            body: JSON.stringify(payload),
        },
        (error) => {
            step.value = 2;
            let message = "Votre rendez-vous n'a pas pu être réservé.";

            if (error?.json) {
                const errorData = error.json;
                if (errorData.seat?.includes("Slot is full") || errorData.non_field_errors?.some(e => e.includes("full"))) {
                    // Le créneau est complet
                    message = "Ce créneau horaire est complet. Veuillez choisir un autre horaire.";
                } else if (errorData.non_field_errors?.some(e => e.includes("unique"))) {
                    // L'utilisateur a déjà une inscription sur ce slot
                    message = "Vous avez déjà une réservation sur ce créneau.";
                } else if (errorData.user_email) {
                    // Erreur liée à l'email
                    message = `Erreur avec l'adresse email: ${errorData.user_email}`;
                } else if (errorData.slot) {
                    // Erreur liée au slot
                    message = "Le créneau sélectionné n'est plus disponible. Veuillez en choisir un autre.";
                } else {
                    // Autre erreur - afficher les détails
                    const details = Object.entries(errorData)
                        .map(([key, val]) => `${key}: ${Array.isArray(val) ? val.join(", ") : val}`)
                        .join(" | ");
                    if (details) {
                        message = `Erreur: ${details}`;
                    }
                }
            }

            $q.notify({
                type: "negative",
                position: "center",
                message: message,
                timeout: 0,
                closeBtn: true,
            });
        }
    ).then(resp => {
        if (resp) {
            registration.value = resp;
            step.value = 4;
        }
    });
}

function cancelRegistration() {
    $q.dialog({
        title: "Annulation?",
        message: `Annuler votre rendez-vous du ${toDate(selectedSlot.value.start)} à ${toTime(
            selectedSlot.value.start
        )}?`,
        cancel: "Non",
        ok: "Oui",
        persistent: true,
    }).onOk(async () => {
        const url = `${window._env_.API_ENDPOINT}/registrations/${registration.value.pk}`;
        const result = await fetchWithHeaders(url, {
            method: "DELETE",
        });
        if (result === null) return;
        step.value = 1;
        delete registration.value.pk;
        delete registration.value.slot;
        delete registration.value.seat;
        selectedSlot.value = null;
        $q.notify({
            type: "positive",
            position: "center",
            message: "Votre rendez-vous a été annulé.",
            timeout: 0,
            closeBtn: true,
        });
    });
}
</script>

<template>
    <div :class="{ 'q-pa-md': $q.screen.gt.sm }">
        <h2 class="text-h4 text-center">
            Demande de rendez-vous
            <span v-if="selectedSession">pour session {{ sessionFormat(selectedSession.code) }}</span>
        </h2>
        <q-card v-if="sessions && sessions.length === 0">
            <q-card-section>
                <h2 class="text-h4 text-center text-negative">Actuellement aucune consultation n'est ouverte.</h2>
            </q-card-section>
        </q-card>
        <div v-if="sessions && sessions.length > 0 && !selectedSession">
            <q-card class="session-select-box q-mt-lg q-mx-auto">
                <q-card-section>
                    <div class="text-h6">Choix de la consultation</div>
                </q-card-section>
                <q-card-section>
                    <q-list bordered separator>
                        <q-item
                            v-for="session in sessions"
                            :key="session.code"
                            v-ripple
                            clickable
                            @click="selectedSession = session"
                        >
                            <q-item-section class="text-bold">{{ sessionFormat(session.code) }}</q-item-section>
                        </q-item>
                    </q-list>
                </q-card-section>
            </q-card>
        </div>
        <q-card v-if="isNoShow">
            <q-card-section>
                <h2 class="text-h4 text-center text-negative">
                    À la suite de votre absence non-justifiée à votre précédente réservation, vous n'êtes plus autorisé
                    à réserver une nouvelle consultation pour cette session.
                </h2>
            </q-card-section>
        </q-card>
        <q-stepper
            v-if="selectedSession && !isNoShow"
            ref="stepper"
            v-model="step"
            animated
            :contracted="$q.screen.lt.md"
            :header-nav="step < 4"
            active-color="primary"
        >
            <q-step :name="1" prefix="1" title="Informations personnelles" :header-nav="step > 1" :done="step > 1">
                <div class="text-h5">Informations personnelles</div>
                <q-form @submit="$refs.stepper.next()">
                    <q-input
                        v-model="registration.user_data.surname"
                        type="text"
                        label="Nom de famille *"
                        :rules="[isRequired]"
                        :readonly="auth.isAuthenticated.value && !auth.isStaff.value"
                        required
                    ></q-input>
                    <q-input
                        v-model="registration.user_data.givenName"
                        type="text"
                        label="Prénom *"
                        :rules="[isRequired]"
                        :readonly="auth.isAuthenticated.value && !auth.isStaff.value"
                        required
                    ></q-input>
                    <q-input
                        v-model="registration.user_email"
                        type="email"
                        label="Adresse e-mail *"
                        :rules="[isRequired, validateEmail]"
                        :readonly="auth.isAuthenticated.value && !auth.isStaff.value"
                        required
                        @blur="checkEmail"
                    ></q-input>
                    <q-banner v-if="isNoShow" class="bg-negative text-white q-my-sm" rounded>
                        A la suite de votre absence non-justifiée à votre précédente réservation, vous n'êtes plus autorisé
                        à réserver une nouvelle consultation pour cette session.
                    </q-banner>
                    <q-banner v-else-if="hasFutureRegistration" class="bg-warning text-dark q-my-sm" rounded>
                        Vous avez déjà une réservation pour cette session.
                    </q-banner>
                    <q-input
                        v-model="registration.user_data.matricule"
                        type="text"
                        label="Matricule *"
                        :rules="[isRequired]"
                        required
                    ></q-input>
                    <q-input
                        v-model="registration.user_data.phone"
                        type="text"
                        label="Téléphone *"
                        :rules="[isRequired]"
                        required
                    ></q-input>
                    <q-input
                        type="text"
                        label="Formation"
                        :model-value="registration.user_data.plan"
                        :readonly="auth.isAuthenticated.value"
                    />
                    <q-input
                        type="text"
                        label="Module d'étude"
                        :model-value="registration.user_data.etape"
                        :readonly="auth.isAuthenticated.value"
                    />
                    <div v-if="registration.user_data.epreuves" class="q-mt-md">
                        <div class="text-caption">Epreuves</div>
                        <ul>
                            <li v-for="(e, index) in registration.user_data.epreuves" :key="index">
                                {{ e.eprnom }}
                            </li>
                        </ul>
                    </div>
                    <q-input
                        v-model="registration.comment"
                        class="q-mt-md"
                        type="textarea"
                        label="Commentaire"
                        filled
                    ></q-input>

                    <q-stepper-navigation class="row">
                        <p>* Champ obligatoire</p>
                        <q-space />
                        <div class="col-6 col-sm-3 col-md-2">
                            <q-btn
                                type="submit"
                                color="primary"
                                class="full-width"
                                label="Suivant"
                                :disable="isNoShow || hasFutureRegistration"
                            />
                        </div>
                    </q-stepper-navigation>
                </q-form>
            </q-step>

            <q-step :name="2" prefix="2" title="Choix de la date et de l'heure" :header-nav="step > 2" :done="step > 2">
                <consultation-slot-select
                    :session="selectedSession"
                    :selected-slot="selectedSlot"
                    @slot="selectedSlot = $event"
                />
                <q-stepper-navigation class="row">
                    <q-space />
                    <div class="col-6 col-sm-3 col-md-2 q-pr-md">
                        <q-btn outline label="Précédent" class="full-width" @click="$refs.stepper.previous()" />
                    </div>
                    <div class="col-6 col-sm-3 col-md-2">
                        <q-btn
                            color="primary"
                            class="full-width"
                            label="Suivant"
                            :disable="!selectedSlot"
                            @click="$refs.stepper.next()"
                        />
                    </div>
                </q-stepper-navigation>
            </q-step>

            <q-step :name="3" prefix="3" title="Récapitulatif" :header-nav="step > 3" :done="step > 3">
                <div class="text-h4">Récapitulatif</div>
                <div class="text-h5 q-py-md">
                    Demande de rendez-vous le
                    <strong>{{ toDate(selectedSlot.start) }} à {{ toTime(selectedSlot.start) }}</strong> pour
                    {{ registration.user_data.surname }} {{ registration.user_data.givenName }}.
                </div>
                <div class="text-h5 q-py-sm">
                    <em>Données de contact</em><br />
                    Email: {{ registration.user_email }}<br />
                    Téléphone: {{ registration.user_data.phone }}
                </div>
                <div class="text-h5 q-py-sm">Commentaire:<br />{{ registration.comment }}</div>
                <q-stepper-navigation class="row">
                    <q-space />
                    <div class="col-6 col-sm-3 col-md-2 q-pr-md">
                        <q-btn outline label="Précédent" class="full-width" @click="$refs.stepper.previous()" />
                    </div>
                    <div class="col-6 col-sm-3 col-md-2">
                        <q-btn color="primary" class="full-width" label="Envoyer" @click="submitRegistration" />
                    </div>
                </q-stepper-navigation>
            </q-step>

            <q-step
                :name="4"
                prefix="4"
                title="Confirmation"
                :header-nav="step > 4"
                active-icon="fa-solid fa-check"
                :done="step > 4"
                class="text-center"
            >
                <div class="text-h5">Confirmation</div>

                <h4>
                    Vous avez rendez-vous le
                    <strong>{{ toDate(selectedSlot.start) }} à {{ toTime(selectedSlot.start) }}</strong
                    >.<br />
                    La consultation à lieu dans la salle <strong>{{ selectedSlot.room }}</strong> à la
                    <strong>place {{ registration.seat }}</strong
                    >.
                </h4>

                <p>
                    <add-to-calendar-button
                        v-if="registration && selectedSlot"
                        :registration="registration"
                        :selected-slot="selectedSlot"
                    />
                </p>

                <p v-if="newRegistration">
                    Un e-mail de confirmation vous sera envoyé à l'adresse {{ registration.user_email }} dans les
                    prochaines minutes
                </p>

                <p v-if="canCancel" class="q-mt-lg">
                    Vous-pouvez encore
                    <q-btn color="negative" outline @click="cancelRegistration">annuler le rendez-vous</q-btn>.
                </p>
            </q-step>
        </q-stepper>
    </div>

    <q-footer>
        <q-toolbar>
            <p class="absolute-center">
                Contact:
                <a
                    class="text-white"
                    href="https://www.unil.ch/hec/bachelors/contacts"
                    target="_blank"
                    rel="noreferrer"
                >
                    Bachelor
                </a>
                &nbsp;
                <a
                    class="text-white"
                    href="https://www.unil.ch/hec/contact/graduate-school-masters"
                    target="_blank"
                    rel="noreferrer"
                >
                    Master
                </a>
            </p>
        </q-toolbar>
    </q-footer>
</template>
<style scoped>
.session-select-box {
    max-width: 500px;
}
</style>
